import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';

import { useCreateFopiTableRow } from '../../hooks/useCreateFopiTableRow';

import { InitFundOperationModalProps } from './InitFundOperationModal';

export type UseInitFundOperationModalPropertiesReturnValue = {
  initFundOperationModalProps?: InitFundOperationModalProps;
  open: () => void;
};

export function useInitFundOperationModalProperties(
  pageId: string,
  contentId: string,
  config: TableConfigDto
): UseInitFundOperationModalPropertiesReturnValue {
  const { t } = useTranslation();
  const [isOpen, { open, close }] = useDisclosure();
  const {
    createFopiTableRow,
    status: createFopiTableRowStatus,
    error,
    reset: resetCreateFopiTableRow,
  } = useCreateFopiTableRow({
    cacheKey: config.cacheKey,
    pageId,
    widgetId: contentId,
  });

  const initFundOperationModalProps = useMemo<
    InitFundOperationModalProps | undefined
  >(() => {
    if (!config.initFundOperationJsonSchema) {
      return undefined;
    }

    if (!isOpen) {
      return undefined;
    }

    const resetStateAndClose = () => {
      resetCreateFopiTableRow();
      close();
    };

    return {
      errorMessage: error?.message,
      jsonSchema: config.initFundOperationJsonSchema,
      onApply: (data, files) => {
        createFopiTableRow(contentId, data, files, () => {
          AsyncNotification.show(
            { message: t('app.initFundOperation.successMessage') },
            'success'
          );
          resetStateAndClose();
        });
      },
      onClose: resetStateAndClose,
      opened: isOpen,
      createFopiTableRowStatus,
    };
  }, [
    isOpen,
    contentId,
    config.initFundOperationJsonSchema,
    createFopiTableRow,
    createFopiTableRowStatus,
    error?.message,
    close,
    resetCreateFopiTableRow,
    t,
  ]);

  return {
    open,
    initFundOperationModalProps,
  };
}
